import React from "react";
import { Button, List, Popover, Switch, Tooltip, Typography } from "antd";
import {
  CheckOutlined,
  CopyOutlined,
  ExclamationCircleTwoTone,
  LoadingOutlined,
} from "@ant-design/icons";
import { Item } from "../../data-types";
import {
  getTransitionTip,
  isCheckedState,
  isEditingNewState,
  isErrorState,
  isTransitionalState,
  ItemState,
} from "./item-state";
import styles from "./check-list.css";

const {
  ["list-item"]: listItemClassName,
  ["item-content"]: itemContentClassName,
  ["item-check"]: itemCheckClassName,
  ["item-message-icon"]: itemMessageIconClassName,
  ["item-text"]: itemTextClassName,
} = styles as any;

export type ListItemProps<T> = {
  itemTextProperty: string;
  item: T;
  checkItem: (item: T) => void;
  uncheckItem: (item: T) => void;
  copyItem: (item: T) => void;
  saveItem: (item: T, text: string) => void;
  itemState?: ItemState;
};

const ERROR_MESSAGE_ICON = <ExclamationCircleTwoTone twoToneColor="#ff4d4f" />;

export const CheckListItem = <T extends Item>({
  itemTextProperty,
  item,
  checkItem,
  uncheckItem,
  copyItem,
  saveItem,
  itemState,
}: ListItemProps<T>) => {
  const { messageTitle, messageContent } = itemState || {};
  const transitionTip = getTransitionTip(itemState);
  const isEditingNew = isEditingNewState(itemState);
  const isChecked = isCheckedState(itemState);
  const isTransitional = isTransitionalState(itemState);
  const editConfig = isEditingNew
    ? {
        autoSize: { minRows: 1, maxRows: 3 },
        maxLength: 1000,
        editing: true,
        onChange: (text: string) => saveItem(item, text),
      }
    : false;
  const messageIcon = isErrorState(itemState) ? ERROR_MESSAGE_ICON : undefined;

  return (
    <List.Item className={listItemClassName}>
      <div className={itemContentClassName}>
        <div className={itemCheckClassName}>
          {!isEditingNew && (
            <Switch
              size="small"
              disabled={isTransitional}
              checked={isChecked}
              checkedChildren={<CheckOutlined />}
              onChange={(checked) => (checked ? checkItem(item) : uncheckItem(item))}
            />
          )}
        </div>
        <div className={itemMessageIconClassName}>
          {isTransitional && (
            <Tooltip placement="right" title={transitionTip}>
              <LoadingOutlined />
            </Tooltip>
          )}
          {!isTransitional && messageIcon && (
            <Popover placement="right" title={messageTitle} content={messageContent}>
              {messageIcon}
            </Popover>
          )}
        </div>
        <div className={itemTextClassName}>
          <Typography.Text
            editable={editConfig}
            copyable={{
              onCopy: () => copyItem(item),
              icon: [
                <Button
                  key="copy"
                  size="small"
                  shape="circle"
                  icon={<CopyOutlined />}
                  disabled={isTransitional}
                />,
                <Button
                  key="copied"
                  size="small"
                  shape="circle"
                  icon={<CopyOutlined />}
                  disabled={isTransitional}
                />,
              ],
            }}
            ellipsis
          >
            {item[itemTextProperty]}
          </Typography.Text>
        </div>
      </div>
    </List.Item>
  );
};
